import { useContext, useRef, useState } from "react";
import { Context as RollbarContext, getRollbarFromContext } from "@rollbar/react";
import { v4 as uuidv4 } from "uuid";
import { isApiError } from "@api";
import {
  attachPhotoAttachmentService,
  deletePhotoAttachmentService,
  getPhotoAttachmentPresignedUrlService,
  updatePhotoAttachmentService,
  uploadPhotoAttachmentFileService,
  PhotoAttachment,
  PhotoAttachmentId,
} from "@Shared/services/photoAttachments";

const MAX_CONCURRENT_UPLOADS = 4;
const S3_RETRY_DELAY_MS = 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const requestErrorDetails = (error: unknown) => {
  if (!isApiError(error)) return {};

  const body = error.response?.data;
  return {
    status: error.response?.status,
    code: error.code,
    responseBody: typeof body === "string" ? body.slice(0, 500) : body,
  };
};

// Only Safari can draw a HEIC: anywhere else its local preview is a broken image.
const HEIC = /\.heic$/i;

const canDraw = (url: string) =>
  new Promise<boolean>((resolve) => {
    const image = new Image();
    image.onload = () => resolve(true);
    image.onerror = () => resolve(false);
    image.src = url;
  });

export interface PendingPhotoUpload {
  id: string;
  file: File;
  blobUrl: string;
}

export interface UsePhotoAttachmentsParams {
  /** e.g. "portfolio" — see the photoAttachments service for why this can't be arbitrary yet. */
  category: string;
  /** Omit for a category (like "task_photos") uploaded before its owner exists —
   * the attachment is created unattached and linked to it later. */
  photoAttachableId?: PhotoAttachmentId;
  /** Caller-owned count of how many more photos can be added; upload() truncates to this. */
  remainingSlots: number;
  /**
   * `previewUrl` is the local preview of the file (a `blob:`, only valid until this call
   * returns — see `uploadFile`), or the server's variant for a HEIC the browser cannot draw.
   */
  onUploadSuccess: (photo: PhotoAttachment, previewUrl: string) => void;
  onUploadError?: (file: File) => void;
  onRemoveSuccess?: (id: PhotoAttachmentId) => void;
  onRemoveError?: (id: PhotoAttachmentId) => void;
  onDescriptionError?: (id: PhotoAttachmentId) => void;
}

/**
 * Queues and uploads multiple photos with a concurrency cap (so a large multi-select
 * doesn't fire dozens of simultaneous presign/upload/attach chains at once), generalizing
 * the multi-upload queue prototyped for the ServiceProvider photo book on the
 * youness/multiple-photo-upload branch: category/photo_attachable_id are parameters here
 * instead of being hardcoded to "portfolio"/Listworker.
 *
 * This hook doesn't own the list of already-attached photos or a max-file count — the
 * caller tracks that and passes `remainingSlots`, and gets results back via
 * onUploadSuccess/onUploadError instead of the hook keeping its own "photos" list.
 *
 * NOT wired into ServiceProviders' live photo book yet: that still runs its own
 * single-file usePhotoBookUpload. Migrating it to this hook is a deliberate follow-up,
 * not bundled in here, since it needs real end-to-end verification against the live
 * upload/attach backend rather than a Storybook-only check.
 */
export const usePhotoAttachments = ({
  category,
  photoAttachableId,
  remainingSlots,
  onUploadSuccess,
  onUploadError,
  onRemoveSuccess,
  onRemoveError,
  onDescriptionError,
}: UsePhotoAttachmentsParams) => {
  const [pendingUploads, setPendingUploads] = useState<PendingPhotoUpload[]>([]);
  const [removingId, setRemovingId] = useState<PhotoAttachmentId | null>(null);
  const queueRef = useRef<PendingPhotoUpload[]>([]);
  const activeCountRef = useRef(0);
  // `withProviders` omits RollBarProvider in the test env, so `useRollbar()`
  // would throw here on every render — read the context directly instead.
  const rollbarContext = useContext(RollbarContext);

  const startNext = () => {
    if (activeCountRef.current >= MAX_CONCURRENT_UPLOADS) return;
    const next = queueRef.current.shift();
    if (!next) return;

    activeCountRef.current += 1;
    uploadFile(next);
  };

  const report = (
    level: "warning" | "error",
    message: string,
    error: unknown,
    file: File,
    details: Record<string, unknown>
  ) => {
    // @rollbar/react types this param as the Context object, but its own
    // useRollbar() passes it useContext(Context)'s resolved value, same as here.
    const rollbar =
      rollbarContext &&
      getRollbarFromContext(
        rollbarContext as unknown as Parameters<typeof getRollbarFromContext>[0]
      );
    rollbar?.[level](
      `[usePhotoAttachments] ${message}`,
      error instanceof Error ? error : new Error(String(error)),
      {
        custom: {
          category,
          photoAttachableId,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          online: navigator.onLine,
          ...requestErrorDetails(error),
          ...details,
        },
      }
    );
  };

  const putToS3 = async (presignedUrl: string, s3FileName: string, file: File) => {
    try {
      await uploadPhotoAttachmentFileService(presignedUrl, file);
      return;
    } catch (error) {
      report("warning", "S3 upload failed, retrying", error, file, { s3FileName, attempt: 1 });
    }

    await wait(S3_RETRY_DELAY_MS);

    try {
      await uploadPhotoAttachmentFileService(presignedUrl, file);
    } catch (error) {
      report("error", "S3 upload failed", error, file, { s3FileName, attempt: 2 });
      throw error;
    }
  };

  const uploadFile = async (pending: PendingPhotoUpload) => {
    const { file } = pending;
    let succeeded = false;

    try {
      let presigned;
      try {
        presigned = await getPhotoAttachmentPresignedUrlService({
          category,
          file_extension: file.name.split(".").pop() ?? "",
        });
      } catch (error) {
        report("error", "Presign request failed", error, file, {});
        throw error;
      }

      await putToS3(presigned.presignedUrl, presigned.fileName, file);

      let attached;
      try {
        attached = await attachPhotoAttachmentService({
          category,
          photo: presigned.fileName,
          photo_attachable_id: photoAttachableId,
        });
      } catch (error) {
        report("error", "Attach request failed", error, file, {
          s3FileName: presigned.fileName,
        });
        throw error;
      }

      let previewUrl = pending.blobUrl;
      if (attached.fullUrl && HEIC.test(file.name) && !(await canDraw(previewUrl))) {
        URL.revokeObjectURL(previewUrl);
        previewUrl = attached.fullUrl;
      }
      succeeded = true;
      onUploadSuccess(attached, previewUrl);
    } catch (error) {
      // Request failures were reported at their step; anything else (a malformed
      // response, a throwing onUploadSuccess) would otherwise go unseen.
      if (!isApiError(error)) report("error", "Photo upload failed unexpectedly", error, file, {});
      onUploadError?.(file);
    } finally {
      // A successful caller now owns this blob's lifetime — it's the photo's
      // only preview until this mount ends, so it must outlive this call.
      if (!succeeded) URL.revokeObjectURL(pending.blobUrl);
      setPendingUploads((current) => current.filter((item) => item.id !== pending.id));
      activeCountRef.current -= 1;
      startNext();
    }
  };

  const upload = (files: FileList | File[]) => {
    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    if (filesToUpload.length === 0) return;

    const newPendingUploads = filesToUpload.map((file) => ({
      id: uuidv4(),
      file,
      blobUrl: URL.createObjectURL(file),
    }));

    setPendingUploads((current) => [...current, ...newPendingUploads]);
    queueRef.current.push(...newPendingUploads);
    while (activeCountRef.current < MAX_CONCURRENT_UPLOADS && queueRef.current.length > 0) {
      startNext();
    }
  };

  const remove = async (id: PhotoAttachmentId) => {
    setRemovingId(id);
    try {
      await deletePhotoAttachmentService(id);
      onRemoveSuccess?.(id);
    } catch {
      onRemoveError?.(id);
    } finally {
      setRemovingId(null);
    }
  };

  /**
   * Writes a photo's caption to the attachment itself, where it lives:
   * `metadata.description`, the only field the controller's `update` permits
   * besides `position`.
   *
   * Deliberately not part of `removingId`/`pendingUploads` — those gate the
   * caller's submit, and a caption save fires on the very blur that clicking
   * a submit button causes. Gating on it would disable that button between
   * mousedown and mouseup and the click would never be dispatched. Nothing is
   * riding on it either: the request outlives this component, so navigating
   * away mid-save still lands the caption.
   */
  const saveDescription = async (id: PhotoAttachmentId, description: string) => {
    try {
      await updatePhotoAttachmentService(id, { metadata: { description } });
    } catch {
      onDescriptionError?.(id);
    }
  };

  return {
    upload,
    remove,
    saveDescription,
    isUploading: pendingUploads.length > 0,
    removingId,
    pendingUploads,
  };
};
